import { graphql } from "gatsby"
import React, { Suspense, useMemo } from "react"

import HeroSection from "../components/HeroSection/HeroSection"
import AboutSection from "../components/AboutDigitup/AboutDigitup"

import loadable from '@loadable/component'

import PortfolioSliderSection from "../components/PortfolioSliderSection/PortfolioSliderSection"
import KpiInsightGrid from "../components/KpiInsightGrid/KpiInsightGrid"
import ContactMap from "../components/ContactMap/ContactMap"

import OurServices from "../components/OurServices/OurServices"
import TestimonialCarousel from "../components/Testimonial/Testimonial"
import Faq from "../components/Faq/Faq"

import Seo from "../components/seo"
import { useSchema, useFAQSchema, useTestimonialSchema } from "../hooks"
import Breadcrumb from "../components/Breadcrumb/Breadcrumb"
import ImageSliderPortfolio from "../components/ImageSliderPortfolio/ImageSliderPortfolio"

import HeroSectionAlternateText from "../components/HeroSectionAlternateText/HeroSectionAlternateText"
import HeroSectionCenterTextWithImage from "../components/HeroSectionCenterTextWithImage/HeroSectionCenterTextWithImage"

import AnimatedProcessSection from "../components/AnimatedProcessSection/AnimatedProcessSection"

import InteractiveToolSection from "../components/InteractiveToolSection/InteractiveToolSection"
import EbookHeroSection from "../components/EbookHeroSection/EbookHeroSection"
import SectionParagraph from "../components/SectionParagraph/SectionParagraph"
import FeaturedListBlock from "../components/FeaturedListBlock/FeaturedListBlock"

import ComponentContactUs from "../components/ContactUs/ComponentContactUs"
const ComponentTabularData = loadable(() => import("../components/ComponentTabularData/ComponentTabularData"))
const DashboardSection = loadable(() => import("../components/DashboardSection/DashboardSection"))
import HeroSectionEventLanding from "../components/HeroSectionEventLanding/HeroSectionEventLanding"
import SectionContent from "../components/sections/SectionContent/SectionContent"
import AnimatedBanner from "../components/AnimatedBanner/AnimatedBanner"
import SectionHeroEvent from "../components/SectionHeroEvent/SectionHeroEvent"
import OurProjects from "../components/OurProjects/OurProjects"
import OurDevelopment from "../components/OurDevelopment/OurDevelopment"
import EventOurServices from "../components/EventOurServices/EventOurServices"

import FaqSchemaSection from "../components/FaqSchemaSection/FaqSchemaSection"
const UrlExtractorTool = loadable(() => import("../components/InteractiveToolSection/UrlExtraction/UrlExtractorTool"))

import HiringSection from "../components/HiringSection/HiringSection"

import InformativeCardsSection from "../components/InformativeSection/InformativeCards"

import ImageSlider from "../components/ImageSlider/ImageSlider"
import PortfolioProject from "../components/PortfolioProject/PortfolioProject"

import { getImage } from "gatsby-plugin-image"
import SSRFallbackHead from "../utils/SSRFallbackHead"
import SolutionStepperFlow from "../components/SolutionQuestionFlow/SolutionStepperFlow"
import ComponentTabularDataWithAccordion from "../components/ComponentTabularDataWithAccordion/ComponentTabularDataWithAccordion"
import EnvironmentImpact from "../components/EnvironmentImpact/EnvironmentImpact"
import MoreFromBlog from "../components/sections/MoreFromBlog/MoreFromBlog"

const getFirstSrcFromSrcSet = srcSet => {
  if (!srcSet) return null
  return srcSet.split(",")[0]?.trim().split(" ")[0] || null
}

const buildPreloadProps = imageData => {
  const image = getImage(imageData)
  const fallback = image?.images?.fallback

  const preferredSource =
    image?.images?.sources?.find(source => source.type === "image/webp") ||
    image?.images?.sources?.[0]

  const href = preferredSource
    ? getFirstSrcFromSrcSet(preferredSource?.srcSet)
    : fallback?.src

  if (!href) return null

  return {
    rel: "preload",
    as: "image",
    href,
    imageSrcSet: preferredSource?.srcSet || fallback?.srcSet,
    imageSizes: preferredSource?.sizes || fallback?.sizes,
    type: preferredSource?.type || "image/webp",
    fetchpriority: "high",
  }
}

const extractHeroImageData = sections => {
  if (!sections || !Array.isArray(sections)) return null
  for (const section of sections) {
    switch (section.__typename) {
      case "ContentfulSectionHeroWithAnimatedBanner":
        return section.bannerImage
      case "ContentfulHeroMonitor":
        return section.mobileImage || section.desktopImage
      case "ContentfulSectionHeroPartnerLogos":
        return section.partnerLogosImageMobile || section.partnerLogosImageDesktop
      case "ContentfulSectionHeroWithText":
        return section.mobileHeroImage || section.heroImage
      case "ContentfulSectionHeroCenterTextWithImage":
        return section.mobileImage || section.heroImage
      case "ContentfulSectionHeroAlternateText":
      case "ContentfulSectionHeroGradientVariant":
        return section.heroImage
      default:
        break
    }
  }
  return null
}

export const Head = ({ pageContext, location, data }) => {
  const sections = data?.contentfulDigitupPageTemplate?.sections
  const heroImageData = extractHeroImageData(sections)
  const heroPreload = buildPreloadProps(heroImageData)

  const seoProps = {
    pageContext,
    location,
    data,
    seoTitle: data?.contentfulDigitupPageTemplate?.seoTitle,
    seoDescription:
      data?.contentfulDigitupPageTemplate?.seoDescription?.seoDescription,
  }

  return (
    <>
      <link
        rel="preconnect"
        href="https://images.ctfassets.net"
        key="contentful-images-preconnect"
      />
      <link
        rel="dns-prefetch"
        href="https://images.ctfassets.net"
        key="contentful-images-dns-prefetch"
      />

      {heroPreload?.href && (
        <link {...heroPreload} key="template-hero-preload" />
      )}

      <SSRFallbackHead {...seoProps} />
    </>
  )
}

const Template = ({
  pageContext,
  location,
  data: {
    contentfulDigitupPageTemplate: {
      seoDescription,
      seoTitle,
      slug,
      pageTitle,
      isLandingPage,
      noIndex,
      sections,
      breadcrum,
      navigationTabs,
    },
  },
}) => {
  
  const faqSchema = sections.find(
    section => section.__typename === "ContentfulSectionAccordion"
  )
    ? useFAQSchema({
        sectionTitle: sections.find(
          section => section.__typename === "ContentfulSectionAccordion"
        ).title,
        faqs: sections
          .find(section => section.__typename === "ContentfulSectionAccordion")
          .componentAccordion.map(({ question, answer }) => ({
            question,
            answer,
          })),
      })
    : null

  const testimonialSection = sections.find(
    section => section.__typename === "ContentfulSectionTestimonials"
  )
  const testimonialSchema = testimonialSection
    ? useTestimonialSchema(testimonialSection.testimonials)
    : null


    // console.log("sections", sections);
      const latestBlogs = useMemo(() => {
        const blogCluster = sections.find(
          section =>
            section.__typename === "ContentfulComponentCluster" &&
            section.clusterType === "latestBlogs&popularBlogs"
        )
    
        if (blogCluster) {
          return blogCluster.entriesList
            ?.filter(blog => blog.publishedDateRaw)
            ?.sort(
              (a, b) => new Date(b.publishedDateRaw) - new Date(a.publishedDateRaw)
            )
        }

        return []
      }, [sections])

  return (
    <>
      <Seo
        title={seoTitle}
        isLandingPage={isLandingPage}
        noIndex={noIndex}
        slug={slug}
        schema={[
          useSchema("website", {}),
          useSchema("organization", {}),
          ...(faqSchema ? [faqSchema] : []),
          ...(testimonialSchema ? [testimonialSchema] : []),
        ]}
      />
      {breadcrum && (
        <div className="container c-padding-bottom bg-white">
          <Breadcrumb data={breadcrum} title={pageTitle} />
        </div>
      )}

     

      {sections.map(section => {
        const key = section.id || section.__typename
        switch (section.__typename) {
          case "ContentfulSectionHeroWithText":
            return <HeroSection data={section} key={key} />
          case "ContentfulSectionHeroAlternateText":
            return <HeroSectionAlternateText data={section} key={key} />
          case "ContentfulSectionHeroEventLanding":
            return <HeroSectionEventLanding data={section} key={key} />
          case "ContentfulSectionHeroCenterTextWithImage":
            return <HeroSectionCenterTextWithImage data={section} key={key} />
            return (
              <TestimonialCarousel
                key={key}
                testimonials={section.testimonials}
                type={section.type}
                testimonialImage={section.testimonialImage}
                title={section.title}
                buttonTestimonial={section.buttonTestimonial}
              />
            )
          case "ContentfulContentfulSectionHiring":
            return <HiringSection data={section} />
          case "ContentfulSectionCards":
            return (
              <OurServices key={key} data={section} />
            )

          case "ContentfulSectionContent":
            return (
              <div className="container c-padding" key={key}>
                <SectionContent data={section} />
              </div>
            )

          case "ContentfulComponentFeaturedItems":
            return (
              <div>
                <AboutSection data={section} />
              </div>
            )

 

          case "ContentfulComponentTabularData":
            return <ComponentTabularData data={section} />
          
          case "ContentfulDashboardSection":
            return <DashboardSection key={key} data={section} />

          case "ContentfulComponentFormBuilder":
            return (
              <section id="contact">
                <ComponentContactUs data={section} />
              </section>
            )

          case "ContentfulSectionHeroEvent":
            return (
              <SectionHeroEvent
                data={section}
                key={key}
                onCtaClick={() => {
                  const sections = document.querySelectorAll("#contact-section")
                  if (sections.length > 1) {
                    const yOffset =
                      sections[1].getBoundingClientRect().top + window.scrollY
                    window.scrollTo({ top: yOffset, behavior: "smooth" })
                  }
                }}
              />
            )

          case "ContentfulOurClients":
            return (
              <ImageSlider
                key={key}
                heading={section.heading}
                imagesArray={section.imagesArray}
              />
            )

          case "ContentfulConnectWithUs":
            return (
              <ContactMap
                key={key}
                title={section.title}
                subtitle={section.subtitle}
                location={section.location}
                contactItems={section.contactItems}
                backgroundColor={section.backgroundColor}
              />
            )

          case "ContentfulInformativeCardsSection":
            return <InformativeCardsSection data={section} />
          case "ContentfulSectionAccordion":
            return (
              <Faq
                key={key}
                sectionTitle={section.title}
                sectionParagraph={section.sectionParagraph}
                faqs={section.componentAccordion}
              />
            )
          case "ContentfulFeaturedListBlock":
            return (
              <FeaturedListBlock
                sectionTitle={section.title}
                sectionParagraph={section.sectionParagraph}
                info={section.infoListBlock}
                image={section.image}
              />
            )
          case "ContentfulStickyImageSection":
            return (
              <Suspense fallback={<div>Loading...</div>} key={key}>
         
              </Suspense>
            )

          case "ContentfulFilteredProjectSection":
            return (
              <PortfolioSliderSection
                title={section.title}
                overviewText={section.overviewText}
                projects={section.projects}
                mainTitle={section.mainTitle}
                projectText={section.projectText}
              />
            )

          case "ContentfulOurClientsPortfolio":
            return <ImageSliderPortfolio title={section.title} imageArray={section.imageArray} />

          case "ContentfulSectionEbookHero":
            return <EbookHeroSection key={key} data={section} />

          case "ContentfulPortfolioProject":
            return (
              <PortfolioProject key={key} data={section} />
            )

          case "ContentfulProjectStageSelector":
            return (
              <AnimatedProcessSection key={key} data={section} />
            )

          

          case "ContentfulInteractiveToolSection":
            return (
              <InteractiveToolSection
                key={key}
                featureType={section.featureType}
                {...section}
                pageSlug={slug}
              />
            )

          case "ContentfulSchemaToolSection":
            return <FaqSchemaSection key={key} {...section} />

          case "ContentfulSectionContent":
            return (
              <div className="container c-padding" key={key}>
                <SectionContent data={section} />
              </div>
            )

          case "ContentfulImpactGridSection":
            return (
              <KpiInsightGrid
                key={key}
                title={section.title}
                images={section.images}
                textSection={section.textSection}
              />
            )
          case "ContentfulContentParagraphSection":
            return <SectionParagraph data={section} />

          case "ContentfulSectionHeroWithAnimatedBanner":
            return (
              <>
                <AnimatedBanner data={section} key={key} />
              </>
            )

          case "ContentfulProjectHighlightSlider":
            return <OurProjects data={section} />
          case "ContentfulResearchAndDevelopmentSlider":
            return <OurDevelopment data={section} />
          case "ContentfulOurServices":
            return <EventOurServices data={section} key={key} />
           case "ContentfulComponentTabularDataWithAccordion":
            return <ComponentTabularDataWithAccordion data={section} key={key}/>


          case "ContentfulComponentCluster":
            if (
              section.clusterType === "latestBlogs&popularBlogs" &&
              latestBlogs.length > 0
            ) {
              return (
                <MoreFromBlog
                  key={key}
                  moreFromBlog={latestBlogs}
                  sectionTitle={section.sectionTitle}
                  button={section.button}
                />
              )
            }
          default:
            return null
        }
      })}
      {/* <EnvironmentImpact data={undefined} /> */}
    </>
  )
}

export const TemplateQuery = graphql`
  query TemplateQuery($id: String!) {
    contentfulDigitupPageTemplate(id: { eq: $id }) {
      seoTitle
      slug
      pageTitle
      isLandingPage
      noIndex
      breadcrum {
        title
        slug
      }
      seoDescription {
        seoDescription
      }
      sections {
        __typename
        ...sectionAccordion
        ...sectionHeroWithText
        ...sectionHiring
        #...sectionTeam
        ...sectionTestimonials
        ...filterSliderSection
        ...sectionCards
        ...sectionContact
        ...stickySliderSection
        ...sectionComponentFeaturedItems
        ...sectionImpactGridSection
        ...sectionOurClient
        ...sectionOurClientPortfolio
        ...interactiveToolSection
        ...SectionEbookHero
        ...sectionParagraph
        ...sectionInformativeCardsSection
        ...portfolioProject
        ...sectionHeroAlternateText
        ...sectionHeroCenterTextWithImage
        ...projectStageSelector
        ...featuredListBlock
        ...sectionHeroEventLanding
        ...componentFormBuilder
        ...componentTabularData
        ...sectionFaqSchemaGenerator
        ...sectionContent
        ...animatedHeroAnimatedBannerSection
        ...sectionHeroEvent
        ...projectHighlightSlider
        ...researchAndDevelopmentSlider
        ...ourServices
        ...componentTabularDataWithAccordion
        ...componentCluster
        ...dashboardSection
      }
    }
  }
`

export default Template
